import type { CreatorActivity, SafetyVideoStatus, TikTokMessage } from "@unbox/api";
import { Gift01 } from "@untitledui/icons";
import { Camera } from "lucide-react";
import { useState } from "react";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { CollaborationLink } from "@/components/collaboration-link";
import { VideoDetails } from "@/components/video-details";
import { ProductDetails } from "@/pages/products/product-details";

// Extended collaboration type to support products
export type CollaborationWithProducts = NonNullable<TikTokMessage["collaboration"]> & {
  products?: Array<{
    productId: string;
    productName: string;
    commissionRate?: number;
    thumbnail?: string;
  }>;
};

// Time formatters
export const messageHeaderDateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  hour: "numeric",
  minute: "2-digit",
});

// Number formatters
export const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

// Helper to parse message content
export const parseMessageContent = (content: string, type: string): string => {
  try {
    const parsed = JSON.parse(content);
    if (type === "TEXT") {
      return parsed.content || content;
    }
    if (type === "TARGET_COLLABORATION_CARD") {
      return "[Collaboration Invitation]";
    }
    if (type === "IMAGE") {
      return "[Image]";
    }
    if (type === "PRODUCT_CARD") {
      return "[Product]";
    }
    return `[${type}]`;
  } catch {
    return content;
  }
};

// Helper to render text with clickable links
export const renderTextWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urlTestRegex = /^https?:\/\/[^\s]+$/; // Non-global regex for testing
  const parts = text.split(urlRegex);

  return parts.map((part, i) => {
    const isUrl = urlTestRegex.test(part);
    const key = isUrl ? `link-${part}` : `text-${part}-${i}`;

    return isUrl ? (
      <a key={key} href={part} target="_blank" rel="noopener noreferrer" className="underline break-all">
        {part}
      </a>
    ) : (
      <span key={key}>{part}</span>
    );
  });
};

/**
 * Product card component for displaying product information in messages
 */
export interface ProductCardProps {
  product: NonNullable<TikTokMessage["product"]>;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  return (
    <div className="flex items-center gap-3 p-2 bg-secondary rounded-lg border border-border-primary max-w-[250px]">
      {product.thumbnail ? (
        <img
          src={product.thumbnail}
          alt={product.productName}
          className="w-18 h-18 rounded object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-18 h-18 rounded bg-tertiary flex items-center justify-center flex-shrink-0">
          <Camera className="w-5 h-5 text-quaternary" />
        </div>
      )}
      <span className="text-sm text-primary line-clamp-2 font-medium m-auto">{product.productName}</span>
    </div>
  );
};

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 2,
});

/**
 * Collaboration card component for displaying collaboration invitations
 */
export interface CollaborationCardProps {
  collaboration: CollaborationWithProducts;
}

export const CollaborationCard = ({ collaboration }: CollaborationCardProps) => {
  const products = collaboration.products;
  const hasProducts = products && products.length > 0;

  if (hasProducts) {
    // Show collaboration with products in horizontal layout
    return (
      <div className="flex gap-3 items-center p-2">
        {/* Products in a row */}
        <div className="flex gap-3">
          {products.map((product) => (
            <div key={product.productId} className="flex flex-col items-center gap-1 flex-shrink-0">
              {product.thumbnail ? (
                <img src={product.thumbnail} alt={product.productName} className="w-16 h-16 rounded object-cover" />
              ) : (
                <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-gray-400" />
                </div>
              )}
              {product.commissionRate !== undefined && (
                <span className="text-xs text-secondary font-medium">
                  {percentFormatter.format(product.commissionRate)}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Collaboration info */}
        <div className="flex flex-col gap-0.5">
          {/* Invite cards are always target collaborations (TARGET_COLLABORATION_CARD messages). */}
          <CollaborationLink
            kind="target"
            collaborationId={collaboration.collaborationId}
            className="text-sm text-primary font-medium border-b border-dotted border-black hover:text-brand-secondary transition-colors w-fit"
          >
            {collaboration.name}
          </CollaborationLink>
          <span className="text-xs text-tertiary">
            {collaboration.type === "TOP_CREATOR_PROGRAM" ? "Top Creator Program" : "Open Collaboration"}
          </span>
        </div>
      </div>
    );
  }

  // Show simple collaboration card without products
  return (
    <div className="flex items-center gap-3 p-3 bg-secondary rounded-lg border border-border-primary max-w-[250px]">
      <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
        <Gift01 className="w-5 h-5 text-brand-600" />
      </div>
      <div className="flex flex-col min-w-0">
        <CollaborationLink
          kind="target"
          collaborationId={collaboration.collaborationId}
          className="text-sm text-primary font-medium line-clamp-2 border-b border-dotted border-black hover:text-brand-secondary transition-colors"
        >
          {collaboration.name}
        </CollaborationLink>
        <span className="text-xs text-tertiary">Collaboration Invite</span>
      </div>
    </div>
  );
};

/**
 * Image message component for displaying shared images
 */
export interface ImageMessageProps {
  image: NonNullable<TikTokMessage["image"]>;
}

export const ImageMessage = ({ image }: ImageMessageProps) => {
  const aspectRatio = image.width && image.height ? image.width / image.height : 1;
  const maxWidth = 300;
  const maxHeight = 300;

  let width = maxWidth;
  let height = width / aspectRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return (
    <a href={image.url} target="_blank" rel="noopener noreferrer">
      <img
        src={image.url}
        alt="Shared content"
        className="rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
        style={{ width: `${width}px`, height: `${height}px` }}
      />
    </a>
  );
};

/**
 * Simplified video card component for all video-related activities
 * Used for video_post, safety_event, and gmv_max activities
 */
export interface SimplifiedVideoCardProps {
  videoId: string;
  videoTitle: string;
  thumbnail?: string | null;
  safetyStatus?: SafetyVideoStatus | null;
  gmv?: number | null;
  views?: number | null;
  likes?: number | null;
  sales?: number | null;
}

export const SimplifiedVideoCard = ({
  videoId,
  videoTitle,
  thumbnail,
  safetyStatus,
  gmv,
  views,
  likes,
  sales,
}: SimplifiedVideoCardProps) => {
  const [selectedVideoId, setSelectedVideoId] = useState<string | undefined>(undefined);

  // Truncate video title to max 100 characters (for ~2 lines)
  const maxTitleLength = 100;
  const truncatedTitle =
    videoTitle.length > maxTitleLength ? `${videoTitle.substring(0, maxTitleLength)}...` : videoTitle;

  return (
    <>
      <div className="flex items-stretch min-h-[200px]">
        {/* Left Column: Video Thumbnail */}
        <div className="flex-shrink-0 py-2 pl-2">
          <div className="relative w-[160px] h-full min-h-[200px] rounded overflow-hidden bg-black">
            {thumbnail ? (
              <img src={thumbnail} alt={videoTitle} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 w-full h-full bg-gray-100 flex items-center justify-center">
                <Camera className="w-8 h-8 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Video Info */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Title and Badge Section - with padding */}
          <div className="p-6 flex flex-col gap-3 flex-1">
            {/* Video Title - Clickable with 2-line support */}
            <button
              type="button"
              onClick={() => setSelectedVideoId(videoId)}
              className="text-sm font-medium text-primary border-b border-dotted border-black hover:text-brand-secondary transition-colors line-clamp-2 text-left cursor-pointer"
              style={{ lineHeight: "1.4", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
            >
              {truncatedTitle}
            </button>

            {/* Status Badge */}
            {safetyStatus && (
              <div className="flex items-center">
                <DetailedVideoStatusBadge status={safetyStatus} showInfoIcon={true} />
              </div>
            )}
          </div>

          {/* Metrics Row - with border separator, no horizontal padding */}
          <div className="border-t border-border-primary py-4 px-3">
            <div className="grid grid-cols-4 gap-2">
              <div>
                <div className="text-xs text-center text-tertiary mb-0.5">GMV</div>
                <div className="text-xs text-center font-medium text-primary">
                  {gmv !== undefined && gmv !== null ? `£${gmv}` : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-center text-tertiary mb-0.5">Views</div>
                <div className="text-xs text-center font-medium text-primary">
                  {views !== undefined && views !== null ? numFormatter.format(views) : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-center text-tertiary mb-0.5">Likes</div>
                <div className="text-xs text-center font-medium text-primary">
                  {likes !== undefined && likes !== null ? numFormatter.format(likes) : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-center text-tertiary mb-0.5">Sales</div>
                <div className="text-xs text-center font-medium text-primary">
                  {sales !== undefined && sales !== null ? sales : "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Details Modal */}
      <VideoDetails id={selectedVideoId} onClose={() => setSelectedVideoId(undefined)} />
    </>
  );
};

/**
 * Sample activity card component for sample requests/shipments
 */
export interface SampleActivityCardProps {
  activity: CreatorActivity & { type: "sample" };
}

export const SampleActivityCard = ({ activity }: SampleActivityCardProps) => {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const thumbnail = activity.thumbnail || activity.skuImageUrl;

  // Truncate product name to max 60 characters
  const maxProductNameLength = 60;
  const truncatedProductName =
    activity.productName.length > maxProductNameLength
      ? `${activity.productName.substring(0, maxProductNameLength)}...`
      : activity.productName;

  return (
    <>
      <div className="flex items-start gap-3 p-2">
        {/* Left Column: Product Image */}
        <div className="flex-shrink-0">
          {thumbnail ? (
            <img src={thumbnail} alt={activity.productName} className="w-16 h-16 rounded object-cover" />
          ) : (
            <div className="w-16 h-16 rounded bg-tertiary flex items-center justify-center">
              <Camera className="w-6 h-6 text-quaternary" />
            </div>
          )}
        </div>

        {/* Right Column: Product Name (Clickable) */}
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setSelectedProductId(activity.productId)}
            className="text-sm text-primary font-medium text-left border-b border-dotted border-black hover:text-brand-secondary transition-colors w-fit max-w-full"
            style={{ lineHeight: "1.4" }}
          >
            {truncatedProductName}
          </button>
        </div>
      </div>

      {/* Product Details Modal */}
      {selectedProductId && <ProductDetails id={selectedProductId} onClose={() => setSelectedProductId(null)} />}
    </>
  );
};

/**
 * Collaboration activity card component for collaboration join/removal
 */
export interface CollaborationActivityCardProps {
  activity: CreatorActivity & { type: "collab_join" | "collab_removal" };
  showCollaborationType?: boolean; // Hide for "Accepted Collaboration"
}

export const CollaborationActivityCard = ({
  activity,
  showCollaborationType = true,
}: CollaborationActivityCardProps) => {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // Get products to display (backend now provides products array for both open and target collaborations)
  const productsToDisplay = activity.products || [];

  // Limit to first 4 products for display
  const displayProducts = productsToDisplay.slice(0, 4);

  // Collaboration name
  const collaborationName = activity.collaborationName;

  // Collaboration type label
  const collaborationTypeLabel =
    activity.collaborationType === "target" ? "Target Collaboration" : "Open Collaboration";

  return (
    <>
      <div className="flex items-start gap-3 p-2">
        {/* Left Column: Product Images with Commission Rates */}
        <div className="flex gap-2">
          {displayProducts.map((product, index) => (
            <div key={product.productId || index} className="flex flex-col items-center">
              {/* Product Image */}
              <div className="flex-shrink-0">
                {product.thumbnail ? (
                  <img src={product.thumbnail} alt={product.productName} className="w-18 h-18 rounded object-cover" />
                ) : (
                  <div className="w-18 h-18 rounded bg-tertiary flex items-center justify-center">
                    <Camera className="w-4 h-4 text-quaternary" />
                  </div>
                )}
              </div>
              {/* Commission Rate */}
              {product.commissionRate !== undefined && product.commissionRate !== null && (
                <span className="text-xs text-tertiary mt-1">{percentFormatter.format(product.commissionRate)}</span>
              )}
            </div>
          ))}
        </div>

        {/* Right Column: Collaboration Name and Type */}
        <div className="flex-1 min-w-0 flex flex-col gap-1 m-auto">
          {/* Collaboration Name - Clickable */}
          <CollaborationLink
            kind={activity.collaborationType === "target" ? "target" : "open"}
            collaborationId={activity.collaborationId}
            className="text-sm text-primary font-medium border-b border-dotted border-black hover:text-brand-secondary transition-colors w-fit"
            style={{ lineHeight: "1.4" }}
          >
            {collaborationName}
          </CollaborationLink>

          {/* Collaboration Type (hidden for accepted) */}
          {showCollaborationType && (
            <div className="text-xs text-tertiary" style={{ lineHeight: "1.4" }}>
              {collaborationTypeLabel}
            </div>
          )}
        </div>
      </div>

      {/* Product Details Modal */}
      {selectedProductId && <ProductDetails id={selectedProductId} onClose={() => setSelectedProductId(null)} />}
    </>
  );
};
